/**
 * PostnlDagplanning — günlük PostNL portal scraper'ından alınan shift +
 * ritten snapshot'larını gösteren sayfa.
 *
 * Akış:
 *   - Sayfa açıldığında `GET /api/postnl/dagplanning?limit=30` çağırır;
 *     son 30 snapshot'ı tarih bazlı kartlarda listeler.
 *   - "Tur Çek" butonu `POST /api/postnl/dagplanning/fetch` çağırır;
 *     süreç 30-90s aralığında. UI bu süre boyunca aşama mesajlarını gösterir
 *     ("Bağlanılıyor… → Giriş yapıldı → Shift 02 alınıyor (1/3)…").
 *   - Bir snapshot'a tıklayınca detay modal açılır: shift'ler, ritten satırları,
 *     CSV indirme linkleri.
 *
 * Tasarım: branding'le tutarlı kart-tabanlı layout. Mevcut planning sayfasını
 * etkilemiyor; yalnızca yeni endpoint'leri tüketiyor.
 */
import React, { useEffect, useMemo, useState } from 'react';
import PostnlPageTabs from '../components/PostnlPageTabs';
import { api } from '../lib/auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, AlertCircle, CheckCircle2, FileDown, X,
  Calendar, Clock, Package as PackageIcon, Trash2, Eye, ChevronDown, ChevronRight,
  Building2, Filter, Search,
} from 'lucide-react';

// "1,04" / "1.250,5" / 1.04 → sayı
const toNum = (v) => {
  if (typeof v === 'number') return v;
  if (v == null || v === '') return null;
  const s = String(v).trim();
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? n : null;
};
const sumOf = (rows, k) => rows.reduce((a, r) => a + (toNum(r[k]) || 0), 0);
const fmtNl = (n, dec) => n.toLocaleString('nl-BE', { minimumFractionDigits: dec, maximumFractionDigits: dec });

function RideTotals({ rides, testId }) {
  if (!rides.length) return null;
  const cell = 'px-2 py-1.5 text-right tabular-nums';
  return (
    <tfoot className="border-t-2 border-slate-300 dark:border-slate-600 bg-orange-50/60 dark:bg-orange-950/20 font-bold text-slate-800 dark:text-slate-100" data-testid={testId}>
      <tr>
        <td />
        <td className="px-2 py-1.5" colSpan={2}>Toplam · {rides.length} rota</td>
        <td className={cell} data-testid={`${testId}-stops`}>{fmtNl(sumOf(rides, 'stops'), 0)}</td>
        <td className={cell} data-testid={`${testId}-stuks`}>{fmtNl(sumOf(rides, 'stuks'), 0)}</td>
        <td className={cell} data-testid={`${testId}-volume`}>{fmtNl(sumOf(rides, 'volume'), 2)}</td>
        <td className={cell} data-testid={`${testId}-gewicht`}>{fmtNl(sumOf(rides, 'gewicht'), 2)}</td>
        <td />
      </tr>
    </tfoot>
  );
}


// -------- Tarih preset yardımcıları --------
function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function startOfWeek(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonth(d) { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function endOfMonth(d) { const x = new Date(d); x.setMonth(x.getMonth() + 1, 0); x.setHours(0, 0, 0, 0); return x; }

const DATE_PRESETS = [
  { id: 'today', label: 'Bugün', range: () => { const t = new Date(); return [toISO(t), toISO(t)]; } },
  { id: 'yesterday', label: 'Dün', range: () => { const y = addDays(new Date(), -1); return [toISO(y), toISO(y)]; } },
  { id: 'this_week', label: 'Bu Hafta', range: () => { const s = startOfWeek(new Date()); const e = addDays(s, 6); return [toISO(s), toISO(e)]; } },
  { id: 'last_week', label: 'Geçen Hafta', range: () => { const s = addDays(startOfWeek(new Date()), -7); const e = addDays(s, 6); return [toISO(s), toISO(e)]; } },
  { id: 'this_month', label: 'Bu Ay', range: () => { const s = startOfMonth(new Date()); const e = endOfMonth(new Date()); return [toISO(s), toISO(e)]; } },
  { id: 'last_month', label: 'Geçen Ay', range: () => { const t = new Date(); t.setMonth(t.getMonth() - 1); return [toISO(startOfMonth(t)), toISO(endOfMonth(t))]; } },
  { id: 'last_30', label: 'Son 30 Gün', range: () => [toISO(addDays(new Date(), -30)), toISO(new Date())] },
  { id: 'all', label: 'Tümü', range: () => [null, null] },
  { id: 'custom', label: 'Özel Aralık', range: null },
];

const STATUS_TR = {
  init: 'Başlatılıyor',
  login: 'Giriş yapılıyor',
  portal: 'OOM portala bağlanılıyor',
  dagplanning: 'Dagplanning açılıyor',
  shift: 'Shift verileri alınıyor',
  rides: 'Rotalar okunuyor',
  csv: 'CSV indiriliyor',
  save: 'Kaydediliyor',
  done: 'Tamamlandı',
  error: 'Hata',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function downloadCsvBlob(b64, filename) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  const blob = new Blob([arr], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PostnlDagplanning() {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [progress, setProgress] = useState(null); // {stage, message, ...}
  const [errorBanner, setErrorBanner] = useState(null);
  const [openSnapshot, setOpenSnapshot] = useState(null); // detail modal
  // Credentials
  const [credentials, setCredentials] = useState([]);
  const [credentialId, setCredentialId] = useState('all'); // 'all' = filtre yok
  // Date filter
  const [presetId, setPresetId] = useState('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Şub 16, 2026 — Tarih filtresi hangi kolona uygulanacak? 'captured' = scraper
  // çekme zamanı (captured_at), 'portal' = PostNL'in dagplanning günü (portal_date).
  // Operatör hangi senaryoya odaklandığını seçebilsin.
  const [dateField, setDateField] = useState(() => {
    try { return localStorage.getItem('postnl_dp_date_field') || 'captured'; } catch { return 'captured'; }
  });
  // Haz 14, 2026 — Arama: rota kodu (örn. "513" veya "0513"), ritnaam,
  // chauffeur, eigenaar yada herhangi bir hücre değerinde substring eşleşmesi.
  const [searchQuery, setSearchQuery] = useState('');
  // Fetch modal — birden çok firma seçilebilir; sıralı çekim yapılır (Şub 16, 2026)
  const [fetchModalOpen, setFetchModalOpen] = useState(false);
  const [fetchCredentialIds, setFetchCredentialIds] = useState([]);
  const [forceFetch, setForceFetch] = useState(false);
  // Çoklu çekimde firmalar arası bekleme (PostNL portalına nazik olmak için)
  const FIRM_GAP_SECONDS = 6;
  // Hedef tarih — portal'daki "Dag" selectbox'ında seçilecek gün (default: bugün)
  const [fetchTargetDate, setFetchTargetDate] = useState(() => toISO(new Date()));

  // Resolve current date range based on preset/custom
  const dateRange = useMemo(() => {
    if (presetId === 'custom') return [customFrom || null, customTo || null];
    const p = DATE_PRESETS.find((x) => x.id === presetId);
    return p?.range ? p.range() : [null, null];
  }, [presetId, customFrom, customTo]);

  const loadCredentials = async () => {
    try {
      const res = await api.get('/postnl/credentials');
      setCredentials(res.data?.data || []);
    } catch {
      // sessiz — boş liste UI'da uyarı gösterecek
    }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      const [df, dt] = dateRange;
      if (df) params.date_from = df;
      if (dt) params.date_to = dt;
      // Tarih filtresinin hangi kolona uygulanacağını backend'e bildir
      params.date_field = dateField;
      if (credentialId && credentialId !== 'all') params.credential_id = credentialId;
      const res = await api.get('/postnl/dagplanning', { params });
      setSnapshots(res.data?.data || []);
    } catch {
      toast.error('Snapshot listesi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCredentials(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadList(); }, [credentialId, presetId, customFrom, customTo, dateField]);

  // dateField değiştiğinde localStorage'a kaydet (kullanıcı tercihi kalıcı olsun)
  useEffect(() => {
    try { localStorage.setItem('postnl_dp_date_field', dateField); } catch { /* ignore */ }
  }, [dateField]);

  // "Tur Çek" — fetch modal aç. Default seçimler:
  //  • Aktif firma sayısı = 1 → o firma otomatik seçili.
  //  • Filtre'de spesifik firma seçili → sadece o firma seçili.
  //  • Diğer durumlarda → seçim boş başlar (kullanıcı isteyerek seçer).
  const openFetchModal = () => {
    if (credentials.length === 0) {
      toast.error('Önce PostNL Hesapları sayfasından bir hesap ekleyin');
      return;
    }
    const activeCreds = credentials.filter((c) => c.active);
    if (activeCreds.length === 0) {
      toast.error('Aktif bir PostNL hesabı yok');
      return;
    }
    let defaults = [];
    if (activeCreds.length === 1) {
      defaults = [activeCreds[0].id];
    } else if (credentialId && credentialId !== 'all' && activeCreds.some((c) => c.id === credentialId)) {
      defaults = [credentialId];
    }
    setFetchCredentialIds(defaults);
    // Modal her açılışta bugünü seç
    setFetchTargetDate(toISO(new Date()));
    setFetchModalOpen(true);
  };

  const toggleFetchCredential = (id) => {
    setFetchCredentialIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllFetchCredentials = () => {
    const activeIds = credentials.filter((c) => c.active).map((c) => c.id);
    setFetchCredentialIds(activeIds);
  };

  const clearFetchCredentials = () => setFetchCredentialIds([]);

  // Tek bir firma için scrape + polling. Resolve → {ok, summary, code, message}.
  // handleFetch'in (çoklu döngü) iç adımı. UI state yan etkileri (progress,
  // errorBanner, toast) burada yönetilir — caller sadece toplam özetler.
  const runFetchForCredential = async (cred, opts = {}) => {
    const { index = 1, total = 1 } = opts;
    const prefix = total > 1 ? `[${index}/${total}] ` : '';
    setProgress({ stage: 'init', message: `${prefix}${cred.label}: portala bağlanılıyor…` });

    let pollInterval = null;
    const cleanup = () => {
      if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
    };

    try {
      const res = await api.post('/postnl/dagplanning/fetch', {
        credential_id: cred.id,
        force: forceFetch,
        target_date: fetchTargetDate || undefined,
      });
      const data = res.data?.data || {};

      // Cache hit / hemen tamamlandı
      if (data.status === 'completed') {
        const summary = data.summary || {};
        const msg = data.cached
          ? `${prefix}${cred.label}: cache'den (${summary.shift_count || 0} shift · ${summary.ride_count || 0} rota)`
          : `${prefix}${cred.label}: ${summary.shift_count || 0} shift · ${summary.ride_count || 0} rota`;
        setProgress({ stage: 'done', message: msg });
        toast.success(msg);
        return { ok: true, summary, cred };
      }

      // Background job → polling
      const jobId = data.job_id;
      if (!jobId) throw new Error('job_id alınamadı');
      setProgress({ stage: 'login', message: `${prefix}${cred.label}: portala bağlanılıyor…` });

      return await new Promise((resolve) => {
        const pollTimeoutMs = 180000;
        let timedOut = false;
        const timeoutId = setTimeout(() => {
          if (pollInterval) {
            cleanup();
            timedOut = true;
            resolve({ ok: false, cred, code: 'poll_timeout', message: 'Scrape 3 dk içinde tamamlanmadı.' });
          }
        }, pollTimeoutMs);

        pollInterval = setInterval(async () => {
          try {
            const sres = await api.get(`/postnl/dagplanning/fetch-status/${jobId}`);
            const job = sres.data?.data || {};
            const log = job.progress_log || [];
            const last = log[log.length - 1];
            if (last) {
              setProgress({ stage: last.stage || 'shift', message: `${prefix}${cred.label}: ${last.message || '...'}` });
            }
            if (job.status === 'completed') {
              cleanup();
              clearTimeout(timeoutId);
              const summary = job.summary || {};
              const msg = `${prefix}${cred.label}: ${summary.shift_count || 0} shift · ${summary.ride_count || 0} rota`;
              setProgress({ stage: 'done', message: msg });
              toast.success(msg);
              resolve({ ok: true, summary, cred });
            } else if (job.status === 'error') {
              cleanup();
              clearTimeout(timeoutId);
              const code = job.error_code || 'unknown';
              const message = job.error_message || 'Beklenmeyen hata';
              resolve({ ok: false, cred, code, message });
            }
          } catch (pollErr) {
            // Polling hatası — kısa süre tolere et
            console.error('poll error', pollErr);
            if (timedOut) cleanup();
          }
        }, 2000);
      });
    } catch (err) {
      cleanup();
      const detail = err.response?.data?.detail;
      const code = detail?.code || 'unknown';
      const message = detail?.message || err.message || 'Beklenmeyen hata';
      return { ok: false, cred, code, message };
    }
  };

  // Modal'dan onaylanınca scraper'ı tetikle (çoklu firma sıralı)
  const handleFetch = async () => {
    if (fetching || fetchCredentialIds.length === 0) return;
    const queue = credentials.filter((c) => fetchCredentialIds.includes(c.id) && c.active);
    if (queue.length === 0) {
      toast.error('Aktif firma seçili değil');
      return;
    }
    setFetchModalOpen(false);
    setFetching(true);
    setErrorBanner(null);

    const results = [];
    for (let i = 0; i < queue.length; i += 1) {
      const cred = queue[i];
      // eslint-disable-next-line no-await-in-loop
      const result = await runFetchForCredential(cred, { index: i + 1, total: queue.length });
      results.push(result);
      // Firmalar arası bekleme (sadece arada, son firma sonrası beklemeye gerek yok)
      if (i < queue.length - 1) {
        for (let s = FIRM_GAP_SECONDS; s > 0; s -= 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 1000));
          setProgress({
            stage: 'wait',
            message: `Sonraki firma için bekleniyor… (${s}s) — Tamamlanan: ${results.filter((x) => x.ok).length}/${queue.length}`,
          });
        }
      }
    }

    // Liste yenile
    await loadList();

    // Özet rapor
    const ok = results.filter((x) => x.ok);
    const failed = results.filter((x) => !x.ok);
    if (failed.length === 0) {
      setProgress({ stage: 'done', message: `Tüm firmalar tamamlandı (${ok.length}/${queue.length})` });
      if (queue.length > 1) toast.success(`${ok.length} firma başarıyla çekildi`);
    } else if (ok.length === 0) {
      const codes = [...new Set(failed.map((x) => x.code))].join(', ');
      setErrorBanner({ code: codes || 'unknown', message: `${failed.length} firma çekilemedi: ${failed.map((x) => x.cred.label).join(', ')}` });
      setProgress(null);
      toast.error(`${failed.length} firma çekilemedi`);
    } else {
      // Karışık sonuç
      setErrorBanner({
        code: 'partial',
        message: `${ok.length}/${queue.length} firma çekildi. Başarısız: ${failed.map((x) => `${x.cred.label} (${x.code})`).join(', ')}`,
      });
      setProgress({ stage: 'done', message: `${ok.length}/${queue.length} firma tamamlandı, ${failed.length} hatalı.` });
      toast.warning(`${ok.length}/${queue.length} firma çekildi, ${failed.length} hata`);
    }

    setTimeout(() => setProgress(null), 6000);
    setFetching(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu snapshot kalıcı olarak silinecek. Devam?')) return;
    try {
      await api.delete(`/postnl/dagplanning/${id}`);
      setSnapshots((s) => s.filter((x) => x.id !== id));
      toast.success('Silindi');
    } catch {
      toast.error('Silinemedi');
    }
  };

  const grouped = useMemo(() => {
    const m = new Map();
    for (const s of snapshots) {
      const k = s.portal_date || s.captured_at?.slice(0, 10) || 'unknown';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [snapshots]);

  // Haz 14, 2026 — Snapshot içi ride arama. Substring eşleşmesi (case-insensitive)
  // ritnaam / eigenaar / chauffeur / cells üzerinde.
  const normalizedQuery = (searchQuery || '').trim().toLowerCase();
  const findMatchingRides = (snapshot) => {
    if (!normalizedQuery) return [];
    const out = [];
    for (const shift of (snapshot.shifts || [])) {
      for (const ride of (shift.rides || [])) {
        const haystack = [
          ride.ritnaam, ride.eigenaar, ride.chauffeur,
          ...(Array.isArray(ride.cells) ? ride.cells : []),
        ].filter(Boolean).join(' ').toLowerCase();
        if (haystack.includes(normalizedQuery)) {
          out.push({ shift_code: shift.shift_code, ...ride });
        }
      }
    }
    return out;
  };
  // Filtre aktifse sadece eşleşmesi olan snapshot'ları göster.
  const filteredGrouped = useMemo(() => {
    if (!normalizedQuery) return grouped;
    return grouped
      .map(([date, items]) => {
        const kept = items.filter((s) => {
          for (const shift of (s.shifts || [])) {
            for (const ride of (shift.rides || [])) {
              const h = [ride.ritnaam, ride.eigenaar, ride.chauffeur,
                         ...(Array.isArray(ride.cells) ? ride.cells : [])]
                .filter(Boolean).join(' ').toLowerCase();
              if (h.includes(normalizedQuery)) return true;
            }
          }
          return false;
        });
        return [date, kept];
      })
      .filter(([, items]) => items.length > 0);
  }, [grouped, normalizedQuery]);
  const totalMatchedSnapshots = filteredGrouped.reduce((a, [, x]) => a + x.length, 0);

  return (
    <div className="space-y-6" data-testid="postnl-dagplanning-page">
      <PostnlPageTabs />
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <PackageIcon className="h-6 w-6 text-orange-500" />
            PostNL Dagplanning
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Firma ve tarih bazlı PostNL shift/rota snapshot arşivi. "Şimdi Çek" ile manuel tetikleyin.
          </p>
        </div>
        <Button
          onClick={openFetchModal}
          disabled={fetching}
          className="bg-orange-600 hover:bg-orange-700 text-white"
          data-testid="postnl-dagplanning-fetch-btn"
        >
          {fetching
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Çekiliyor…</>
            : <><RefreshCw className="h-4 w-4 mr-2" /> Şimdi Çek</>}
        </Button>
      </div>

      {/* Filter bar: firma + tarih preset + (özel aralık) */}
      <Card className="p-3 flex flex-wrap items-center gap-3" data-testid="postnl-dagplanning-filterbar">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-slate-400" />
          <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Firma:</span>
          <select
            value={credentialId}
            onChange={(e) => setCredentialId(e.target.value)}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            data-testid="postnl-dagplanning-filter-credential"
          >
            <option value="all">Tümü</option>
            {credentials.map((c) => (
              <option key={c.id} value={c.id}>{c.label}{!c.active ? ' (pasif)' : ''}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          {/* Tarih kolonu seçici — captured (scraper çekme zamanı) vs portal (PostNL günü).
              Operatör "geçen hafta çektiklerim" mi yoksa "geçen haftanın dagplanning'i" mi
              filtrelemek istediğine karar verir. Tercih localStorage'da saklanır. */}
          <div
            className="inline-flex rounded border border-slate-200 dark:border-slate-700 overflow-hidden text-xs"
            role="tablist"
            aria-label="Tarih kolonu"
            data-testid="postnl-dagplanning-date-field-toggle"
          >
            <button
              type="button"
              onClick={() => setDateField('captured')}
              className={`px-2 py-1 font-semibold transition-colors ${
                dateField === 'captured'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
              title="Snapshot'ın çekildiği zaman (captured_at)"
              data-testid="postnl-dagplanning-date-field-captured"
              aria-pressed={dateField === 'captured'}
            >
              Çekilme
            </button>
            <button
              type="button"
              onClick={() => setDateField('portal')}
              className={`px-2 py-1 font-semibold transition-colors border-l border-slate-200 dark:border-slate-700 ${
                dateField === 'portal'
                  ? 'bg-orange-500 text-white'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
              title="PostNL'in dagplanning günü (portal_date)"
              data-testid="postnl-dagplanning-date-field-portal"
              aria-pressed={dateField === 'portal'}
            >
              Portal Günü
            </button>
          </div>
          <select
            value={presetId}
            onChange={(e) => setPresetId(e.target.value)}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            data-testid="postnl-dagplanning-filter-preset"
          >
            {DATE_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>
        {presetId === 'custom' && (
          <div className="flex items-center gap-1 text-xs">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs"
              data-testid="postnl-dagplanning-custom-from"
            />
            <span className="text-slate-400">→</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs"
              data-testid="postnl-dagplanning-custom-to"
            />
          </div>
        )}
        <div className="flex-1" />
        {/* Arama: shift+rota içeriği için substring filtresi — rota kodu,
            ritnaam, şoför, eigenaar veya herhangi bir hücre değerinde eşleşir.
            Filtre uygulandığında snapshot kartı eşleşen rota sayısını
            ve ilk 3 eşleşmenin önizlemesini gösterir. */}
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rota / şoför / veri ara (örn. 513, Sigirtmac)"
            className="w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            data-testid="postnl-dagplanning-search"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="text-[11px] text-slate-500 hover:text-slate-700"
              title="Aramayı temizle"
              data-testid="postnl-dagplanning-search-clear"
            >×</button>
          )}
        </div>
        <Badge variant="outline" className="text-[10px] font-mono" title={dateField === 'portal' ? 'Portal günü filtresi' : 'Çekilme zamanı filtresi'}>
          {snapshots.length} snapshot · {dateField === 'portal' ? 'portal' : 'çekildi'} {dateRange[0] || '∞'} → {dateRange[1] || '∞'}
        </Badge>
      </Card>

      {/* Progress indicator */}
      {fetching && progress && (
        <Card className="p-4 border-orange-200 bg-orange-50 dark:bg-orange-900/20" data-testid="postnl-dagplanning-progress">
          <div className="flex items-start gap-3">
            <Loader2 className="h-5 w-5 text-orange-600 animate-spin mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide">
                {STATUS_TR[progress.stage] || progress.stage}
              </div>
              <div className="text-sm text-slate-700 dark:text-slate-200 mt-0.5">{progress.message}</div>
              <div className="mt-2 h-1.5 bg-orange-200 dark:bg-orange-800/40 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 animate-pulse w-2/3" />
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Bu işlem genelde 30-90 saniye sürer. Lütfen sayfayı kapatmayın.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Error banner */}
      {errorBanner && (
        <Card className="p-4 border-rose-300 bg-rose-50 dark:bg-rose-900/20" data-testid="postnl-dagplanning-error">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-rose-700 dark:text-rose-300">{errorBanner.message}</div>
              <div className="text-[11px] text-slate-500 mt-1">Kod: {errorBanner.code}{errorBanner.stage ? ` · Aşama: ${errorBanner.stage}` : ''}</div>
            </div>
            <button onClick={() => setErrorBanner(null)} className="text-slate-400 hover:text-slate-700" data-testid="postnl-dagplanning-error-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </Card>
      )}

      {/* Snapshots */}
      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Yükleniyor…</div>
      ) : snapshots.length === 0 ? (
        <Card className="p-10 text-center" data-testid="postnl-dagplanning-empty">
          <PackageIcon className="h-12 w-12 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">Bu filtre için snapshot bulunamadı. Filtreyi değiştirin veya "Şimdi Çek" ile yeni veri ekleyin.</p>
        </Card>
      ) : filteredGrouped.length === 0 && normalizedQuery ? (
        <Card className="p-8 text-center">
          <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          <p className="text-slate-500 text-sm">&quot;<span className="font-mono font-semibold">{searchQuery}</span>&quot; araması için sonuç yok. Başka bir terim deneyin.</p>
        </Card>
      ) : (
        filteredGrouped.map(([date, items]) => (
          <div key={date} className="space-y-2">
            <h2 className="text-sm font-bold text-slate-600 dark:text-slate-300 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              {date}
              <Badge variant="outline" className="text-[10px]">{items.length} çekim</Badge>
              {normalizedQuery && <span className="text-[10px] text-orange-600 font-mono">· {items.reduce((a, s) => a + findMatchingRides(s).length, 0)} eşleşme</span>}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((s) => (
                <Card
                  key={s.id}
                  className="p-3 cursor-pointer hover:border-orange-300 hover:shadow-md transition"
                  onClick={() => setOpenSnapshot(s)}
                  data-testid={`postnl-dagplanning-snapshot-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-400 flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        Çekildi: <span className="font-mono">{fmtDate(s.captured_at)}</span>
                      </div>
                      {s.portal_date && (
                        <div className="text-[10px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                          <Calendar className="h-2.5 w-2.5" />
                          Portal günü: <span className="font-mono">{s.portal_date}</span>
                        </div>
                      )}
                      {s.credential_label && (
                        <Badge className="mt-1.5 bg-orange-100 text-orange-700 border-0 text-[10px] font-semibold">
                          <Building2 className="h-2.5 w-2.5 mr-1" />
                          {s.credential_label}
                        </Badge>
                      )}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 font-semibold">{(s.shifts || []).length} shift</Badge>
                        <Badge variant="outline" className="font-semibold">
                          {(s.shifts || []).reduce((acc, sh) => acc + (sh.rides || []).length, 0)} rota
                        </Badge>
                        {(s.errors || []).length > 0 && (
                          <Badge className="bg-rose-100 text-rose-700 border-0">{s.errors.length} hata</Badge>
                        )}
                        {/* Health partial: PostNL sayfasında daha fazla rota
                            görünmesine rağmen scrape'te eksik veri varsa
                            sessiz kayıp uyarısı. Tıklanınca detay modal'da
                            atlanan rota kodları görüntülenir. */}
                        {s.health === 'partial' && (
                          <Badge
                            className="bg-amber-100 text-amber-800 border border-amber-300 font-semibold animate-pulse"
                            title={
                              `${s.parsed_ride_count ?? '?'}/${s.expected_ride_count ?? '?'} rota — ` +
                              `${(s.skipped_codes || []).length} atlandı: ${(s.skipped_codes || []).join(', ') || '?'}`
                            }
                            data-testid={`postnl-dagplanning-health-partial-${s.id}`}
                          >
                            ⚠ Eksik: {(s.expected_ride_count || 0) - (s.parsed_ride_count || 0)}
                          </Badge>
                        )}
                      </div>
                      {s.fetched_by_name && (
                        <div className="text-[10px] text-slate-400 mt-1.5">Çeken: {s.fetched_by_name}</div>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}
                      className="text-slate-300 hover:text-rose-500 shrink-0"
                      title="Sil"
                      data-testid={`postnl-dagplanning-delete-${s.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {/* Arama eşleşmeleri — en fazla 3 önizleme + kalan sayısı.
                      Snapshot'a tıklanınca detail modal eşleşmenin neresi
                      olduğunu daha rahat görmeye yarar. */}
                  {normalizedQuery && (() => {
                    const matches = findMatchingRides(s);
                    if (!matches.length) return null;
                    const preview = matches.slice(0, 3);
                    const more = matches.length - preview.length;
                    return (
                      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1">
                        <div className="text-[10px] uppercase tracking-wide text-orange-600 font-semibold">
                          {matches.length} eşleşme
                        </div>
                        {preview.map((m, i) => (
                          <div key={i} className="text-[11px] flex items-center gap-1.5">
                            <Badge variant="outline" className="text-[9px] py-0 px-1 h-4 font-mono">
                              {m.shift_code}
                            </Badge>
                            <span className="font-medium font-mono truncate">{m.ritnaam}</span>
                            {m.chauffeur && <span className="text-slate-400">· {m.chauffeur}</span>}
                          </div>
                        ))}
                        {more > 0 && (
                          <div className="text-[10px] text-slate-500 italic">… ve {more} daha</div>
                        )}
                      </div>
                    );
                  })()}
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Detail modal */}
      {openSnapshot && (
        <SnapshotDetailModal
          snapshot={openSnapshot}
          initialSearch={searchQuery}
          onClose={() => setOpenSnapshot(null)}
        />
      )}

      {/* Fetch firma seçim modal */}
      {fetchModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setFetchModalOpen(false)}>
          <Card className="max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="postnl-dagplanning-fetch-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-orange-500" />
                Hangi firmalar için çekilsin?
              </div>
              <button onClick={() => setFetchModalOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-2">
              {fetchCredentialIds.length <= 1
                ? 'Tek firma seçiliyse scraper tek seferde çalışır (30-90s).'
                : `${fetchCredentialIds.length} firma sırayla çekilir; aralarda ${FIRM_GAP_SECONDS}s bekleme — PostNL'e nazik olmak için.`}
            </p>
            {/* Quick actions */}
            {credentials.filter((c) => c.active).length > 1 && (
              <div className="flex items-center justify-between mb-2 text-xs">
                <div className="text-slate-500">
                  Seçili: <span className="font-semibold text-orange-500">{fetchCredentialIds.length}</span>
                  {' / '}
                  {credentials.filter((c) => c.active).length} aktif
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={selectAllFetchCredentials}
                    className="text-orange-600 hover:text-orange-700 font-medium"
                    data-testid="postnl-dagplanning-fetch-select-all"
                  >
                    Tümünü Seç
                  </button>
                  <span className="text-slate-300">·</span>
                  <button
                    type="button"
                    onClick={clearFetchCredentials}
                    className="text-slate-500 hover:text-slate-700"
                    data-testid="postnl-dagplanning-fetch-clear-all"
                  >
                    Temizle
                  </button>
                </div>
              </div>
            )}
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {credentials.filter((c) => c.active).map((c) => {
                const checked = fetchCredentialIds.includes(c.id);
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2 rounded border cursor-pointer transition ${
                      checked
                        ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                    data-testid={`postnl-dagplanning-fetch-option-${c.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleFetchCredential(c.id)}
                      className="text-orange-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">{c.label}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{c.username}</div>
                    </div>
                    {c.notes && (
                      <div className="text-[10px] text-slate-400 truncate max-w-[120px]">{c.notes}</div>
                    )}
                  </label>
                );
              })}
            </div>
            {/* Hedef tarih — portal'ın "Dag" selectbox'ında seçilecek gün */}
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <label className="block text-sm font-medium mb-1.5 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-orange-500" /> Hedef Gün
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={fetchTargetDate}
                  onChange={(e) => setFetchTargetDate(e.target.value)}
                  className="flex-1 px-3 py-1.5 text-sm border rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                  data-testid="postnl-dagplanning-fetch-target-date"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFetchTargetDate(toISO(new Date()))}
                  data-testid="postnl-dagplanning-fetch-target-today-btn"
                >
                  Bugün
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFetchTargetDate(toISO(addDays(new Date(), 1)))}
                  data-testid="postnl-dagplanning-fetch-target-tomorrow-btn"
                >
                  Yarın
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Portal'daki "Dag" alanında bu tarih otomatik seçilir. PostNL genelde
                bugün + ileri 2 hafta gösterir.
              </div>
            </div>
            {/* Force option — son 3 saatte snapshot varsa cache döner; force ile zorla yenile */}
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={forceFetch}
                  onChange={(e) => setForceFetch(e.target.checked)}
                  className="mt-0.5 text-orange-500"
                  data-testid="postnl-dagplanning-force-checkbox"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">Zorla Yeniden Çek</div>
                  <div className="text-[11px] text-slate-500">
                    Varsayılan: son 3 saat içinde snapshot varsa cache'den döner.
                    Bu seçenek aktif olursa portala yeniden bağlanılır.
                  </div>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setFetchModalOpen(false)}>İptal</Button>
              <Button
                onClick={handleFetch}
                disabled={fetchCredentialIds.length === 0}
                className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
                data-testid="postnl-dagplanning-fetch-confirm-btn"
              >
                <RefreshCw className="h-4 w-4" />
                {fetchCredentialIds.length > 1
                  ? `${fetchCredentialIds.length} Firmayı Sırayla Çek`
                  : 'Çekmeyi Başlat'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function SnapshotDetailModal({ snapshot, onClose, initialSearch = '' }) {
  const [full, setFull] = useState(null);
  // Map<ritnaam, {loading, data, error}>
  const [parsedMap, setParsedMap] = useState({});
  const [expanded, setExpanded] = useState({}); // ritnaam → bool
  // Haz 14, 2026 — Modal-içi arama. Liste sayfasından gelen `initialSearch`
  // ile pre-fill edilir (kullanıcı arama yapıp snapshot'a tıklarsa).
  const [search, setSearch] = useState(initialSearch || '');
  const q = (search || '').trim().toLowerCase();
  const matchesRide = (ride) => {
    if (!q) return true;
    const h = [
      ride.ritnaam, ride.eigenaar, ride.chauffeur,
      ...(Array.isArray(ride.cells) ? ride.cells : []),
    ].filter(Boolean).join(' ').toLowerCase();
    return h.includes(q);
  };
  const totalMatched = (full?.shifts || []).reduce(
    (a, sh) => a + (sh.rides || []).filter(matchesRide).length, 0,
  );

  // Eşleşen substring'i sarı arka planla vurgular. q yoksa text aynen döner.
  const renderHighlight = (text) => {
    const s = text == null ? '' : String(text);
    if (!q || !s) return s;
    const lower = s.toLowerCase();
    let idx = lower.indexOf(q);
    if (idx === -1) return s;
    const parts = [];
    let cursor = 0;
    let key = 0;
    while (idx !== -1) {
      if (idx > cursor) parts.push(<span key={key++}>{s.slice(cursor, idx)}</span>);
      parts.push(
        <mark key={key++} className="bg-yellow-200 dark:bg-yellow-500/40 text-inherit rounded-sm px-0.5">
          {s.slice(idx, idx + q.length)}
        </mark>
      );
      cursor = idx + q.length;
      idx = lower.indexOf(q, cursor);
    }
    if (cursor < s.length) parts.push(<span key={key++}>{s.slice(cursor)}</span>);
    return parts;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/postnl/dagplanning/${snapshot.id}`);
        if (!cancelled) setFull(res.data?.data);
      } catch {
        toast.error('Detay alınamadı');
      }
    })();
    return () => { cancelled = true; };
  }, [snapshot.id]);

  const toggleExpand = async (ritnaam) => {
    const willOpen = !expanded[ritnaam];
    setExpanded((m) => ({ ...m, [ritnaam]: willOpen }));
    if (!willOpen) return;
    if (parsedMap[ritnaam]?.data) return; // cache hit
    setParsedMap((m) => ({ ...m, [ritnaam]: { loading: true } }));
    try {
      const res = await api.get(
        `/postnl/dagplanning/${snapshot.id}/csv/${encodeURIComponent(ritnaam)}/parsed`,
      );
      setParsedMap((m) => ({ ...m, [ritnaam]: { data: res.data?.data } }));
    } catch (err) {
      setParsedMap((m) => ({
        ...m,
        [ritnaam]: { error: err?.response?.data?.detail || 'Parse hatası' },
      }));
    }
  };

  const downloadCsv = async (snapId, ritnaam) => {
    try {
      const res = await api.get(`/postnl/dagplanning/${snapId}/csv/${encodeURIComponent(ritnaam)}`);
      const b64 = res.data?.data?.csv_base64;
      if (!b64) {
        toast.error('CSV bulunamadı');
        return;
      }
      downloadCsvBlob(b64, `${ritnaam.replace(/[^\w-]+/g, '_')}.csv`);
    } catch {
      toast.error('CSV indirilemedi');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
          <PackageIcon className="h-5 w-5 text-orange-500" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold">{snapshot.portal_date}</h3>
            <p className="text-xs text-slate-500">{fmtDate(snapshot.captured_at)}</p>
          </div>
          {/* Modal-içi arama — eşleşmeyen ride'lar gizlenir, shift başlığı
              sayacı otomatik güncellenir. */}
          <div className="flex items-center gap-1.5">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Modal içinde ara (örn. 513)"
              className="w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-orange-300"
              data-testid="postnl-dagplanning-modal-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-[11px] text-slate-500 hover:text-slate-700"
                title="Aramayı temizle"
              >×</button>
            )}
            {q && (
              <Badge variant="outline" className="text-[10px] font-mono">
                {totalMatched}
              </Badge>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="postnl-dagplanning-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!full ? (
            <div className="text-center py-8 text-slate-400 text-sm">Yükleniyor…</div>
          ) : (
            <>
              {/* Snapshot health uyarı banner'ı — sessiz veri kaybı tespit
                  edildiğinde tüm atlanan rota kodlarını listeler. */}
              {full.health === 'partial' && (
                <div
                  className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 flex items-start gap-3"
                  data-testid="postnl-dagplanning-health-banner"
                >
                  <div className="text-amber-700 dark:text-amber-300 text-lg leading-none">⚠</div>
                  <div className="flex-1 text-xs text-amber-900 dark:text-amber-100">
                    <div className="font-semibold mb-0.5">
                      Eksik veri tespit edildi: {full.parsed_ride_count ?? 0}/{full.expected_ride_count ?? 0} rota çekilebildi
                    </div>
                    <div className="opacity-90">
                      PostNL sayfasında bulunan {(full.expected_ride_count || 0) - (full.parsed_ride_count || 0)} rota
                      scrape edilirken atlandı.
                      {(full.skipped_codes || []).length > 0 && (
                        <span> Atlanan kodlar: <span className="font-mono font-semibold">{(full.skipped_codes || []).join(', ')}</span></span>
                      )}
                      <span className="block mt-1 opacity-75">&quot;Force=true&quot; ile yeniden çekmeyi deneyin.</span>
                    </div>
                  </div>
                </div>
              )}
              {(full.shifts || []).map((shift) => (
              <Card key={shift.shift_code} className="p-3 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-orange-100 text-orange-700 border-0 font-bold">Shift {shift.shift_code}</Badge>
                  {shift.channel && <span className="text-xs text-slate-500">{shift.channel}</span>}
                  {shift.health === 'partial' && (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px]"
                      title={`${shift.parsed_rides}/${shift.expected_rides} — atlanan: ${(shift.skipped_codes || []).join(', ')}`}
                    >
                      ⚠ {shift.parsed_rides}/{shift.expected_rides}
                    </Badge>
                  )}
                  <span className="ml-auto text-[10px] text-slate-400">
                    {q
                      ? `${(shift.rides || []).filter(matchesRide).length}/${(shift.rides || []).length} eşleşen`
                      : `${(shift.rides || []).length} rota`}
                  </span>
                </div>
                {(shift.rides || []).filter(matchesRide).length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-semibold w-6"></th>
                          <th className="text-left px-2 py-1.5 font-semibold">Rota</th>
                          <th className="text-left px-2 py-1.5 font-semibold">Şoför</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Stops</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Stuks</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Volume</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Gewicht</th>
                          <th className="text-right px-2 py-1.5 font-semibold">CSV</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shift.rides.filter(matchesRide).map((r, idx) => {
                          const hasCsv = (shift.csv_exports || []).some((c) => c.ritnaam === r.ritnaam);
                          const isOpen = !!expanded[r.ritnaam];
                          const parsed = parsedMap[r.ritnaam];
                          return (
                            <React.Fragment key={`${shift.shift_code}-${idx}`}>
                              <tr className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                                <td className="px-2 py-1.5">
                                  {hasCsv && (
                                    <button
                                      onClick={() => toggleExpand(r.ritnaam)}
                                      className="text-slate-400 hover:text-orange-600"
                                      title={isOpen ? 'Daralt' : 'CSV içeriğini göster'}
                                      data-testid={`postnl-csv-toggle-${shift.shift_code}-${idx}`}
                                    >
                                      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                    </button>
                                  )}
                                </td>
                                <td className="px-2 py-1.5 font-mono text-slate-700 dark:text-slate-200">{r.ritnaam ? renderHighlight(r.ritnaam) : '—'}</td>
                                <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">{r.chauffeur ? renderHighlight(r.chauffeur) : '—'}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{r.stops ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{r.stuks ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{r.volume ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{r.gewicht ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right">
                                  {hasCsv ? (
                                    <button
                                      onClick={() => downloadCsv(full.id, r.ritnaam)}
                                      className="text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1"
                                      title="Ham CSV indir"
                                      data-testid={`postnl-dagplanning-csv-${shift.shift_code}-${idx}`}
                                    >
                                      <FileDown className="h-3.5 w-3.5" />
                                    </button>
                                  ) : <span className="text-slate-300">—</span>}
                                </td>
                              </tr>
                              {isOpen && (
                                <tr className="bg-slate-50/60 dark:bg-slate-900/40">
                                  <td colSpan={8} className="px-3 py-2">
                                    {parsed?.loading && (
                                      <div className="text-slate-500 text-[11px] flex items-center gap-1">
                                        <Loader2 className="h-3 w-3 animate-spin" /> Yükleniyor…
                                      </div>
                                    )}
                                    {parsed?.error && (
                                      <div className="text-rose-600 text-[11px]">Hata: {String(parsed.error)}</div>
                                    )}
                                    {parsed?.data && (
                                      <ParsedCsvTable parsed={parsed.data} />
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <RideTotals rides={shift.rides.filter(matchesRide)} testId={`postnl-shift-totals-${shift.shift_code}`} />
                    </table>
                  </div>
                )}
              </Card>
            ))}
            </>
          )}
          {full?.errors?.length > 0 && (
            <Card className="p-3 border-rose-200 bg-rose-50 dark:bg-rose-900/20">
              <div className="text-sm font-semibold text-rose-700 mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" /> Hatalar
              </div>
              <ul className="text-xs space-y-1">
                {full.errors.map((e, i) => (
                  <li key={i} className="text-rose-600">
                    {e.shift ? `Shift ${e.shift}: ` : ''}{e.error}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {full?.progress_log && full.progress_log.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700 font-semibold">
                Aşama günlüğü ({full.progress_log.length})
              </summary>
              <div className="mt-2 p-2 bg-slate-50 dark:bg-slate-900 rounded font-mono text-[10px] max-h-48 overflow-y-auto space-y-0.5">
                {full.progress_log.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-slate-400">{p.ts?.slice(11, 19)}</span>
                    <span className="text-emerald-600">[{p.stage}]</span>
                    <span className="text-slate-700 dark:text-slate-200">{p.message}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
        <div className="p-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 bg-slate-50 dark:bg-slate-900">
          <Button variant="ghost" onClick={onClose}>Kapat</Button>
          {full && <Badge variant="outline" className="text-[10px]">{full.duration_seconds?.toFixed(1)}s</Badge>}
          {full && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
        </div>
      </Card>
    </div>
  );
}

function ParsedCsvTable({ parsed }) {
  const headers = parsed.headers || [];
  const rows = parsed.rows || [];
  if (!rows.length) {
    return <div className="text-[11px] text-slate-400">Bu rota için satır bulunamadı.</div>;
  }
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1 bg-slate-100 dark:bg-slate-800 text-[10px] text-slate-600 dark:text-slate-300">
        <span className="font-semibold">{parsed.ritnaam} — {rows.length} stop</span>
        <span className="text-slate-400">UTF-8 decoded · CSV içeriği</span>
      </div>
      <div className="overflow-x-auto max-h-72 overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead className="sticky top-0 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300">
            <tr>
              {headers.map((h) => (
                <th key={h} className="text-left px-2 py-1 font-semibold border-b border-slate-200 dark:border-slate-700 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="odd:bg-slate-50/40 dark:odd:bg-slate-900/40">
                {headers.map((h) => (
                  <td key={h} className="px-2 py-1 whitespace-nowrap text-slate-700 dark:text-slate-200">
                    {r[h] || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

